import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import Switch from '@material-ui/core/Switch';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';

import { DefaultInput } from 'shared/components/text-input-with-label';
import useCart from 'hooks/use-cart';
import { useLoyaltyV1 } from 'components/widgets/loyalty-tiers/use-loyaltyV1';
import { DposApplyRewardsCheckout } from 'components/loyalty/rewards/dpos-apply-rewards-checkout';
import { RedemptionType, Mode } from 'components/loyalty/rewards/dpos-utils';

export const LoyaltyWidget = observer(
  (): JSX.Element => {
    const flags = useFlags();
    const Cart = useCart();
    const dposRewardRedemption = flags['growth.ecomm.dpos-loyalty-rewards-redemption.rollout'] ?? false;
    const dposRewardPostAndPreTaxRedemption =
      flags['growth.ecomm.dpos-loyalty-rewards-post-and-pre-tax-redemption.rollout'] ?? false;
    const [pointsApplied, setPointsApplied] = useState<string>('0');
    const [applyMyRewards, setApplyMyRewards] = useState<boolean>(false);
    const { dispensary, loyaltyPointsApplied } = Cart;
    const { loyaltyConfig, rewardsArray, posCustomerId } = useLoyaltyV1();
    const redemptionType = loyaltyConfig?.redemptionType;
    const mode = loyaltyConfig?.mode;

    const handleApplyReward = (cost: number, copy: string, id: string): void => {
      // Find the reward to get its value
      const reward = rewardsArray.find((r) => r.id === id);
      const rewardValue = reward?.value;

      const appliedReward = {
        id: reward?.id,
        discountId: reward?.discountId,
        copy,
        value: rewardValue,
        operator: reward?.operator,
        available: reward?.available,
        brand: reward?.brand,
      };

      Cart.applyReward(appliedReward);
      Cart.posCustomerId = posCustomerId ?? '';

      // Also update loyaltyPointsApplied for backwards compatibility
      Cart.loyaltyPointsApplied = cost;
      Cart.loyaltyAppliedAsDiscount = cost;
      setPointsApplied(cost.toString());
      setApplyMyRewards(true);
    };

    const handleUndoReward = (): void => {
      Cart.appliedRewards = [];

      Cart.loyaltyPointsApplied = 0;
      setPointsApplied('0');
    };

    useEffect(() => {
      const pointsAsNumber = Number(pointsApplied);
      if (Number.isNaN(pointsAsNumber)) {
        return;
      }
      if (pointsAsNumber !== loyaltyPointsApplied) {
        setPointsApplied((loyaltyPointsApplied ?? 0).toString());
      }
    }, [pointsApplied, loyaltyPointsApplied]);

    function handleCashRedemption(event: React.ChangeEvent<HTMLInputElement>, resetValue: boolean): void {
      let { value } = event.target;
      value = resetValue ? '0' : value;

      let pointsAsNumber = Number(value);
      const rewardBalanceNumber = Number(Cart.rewardsBalance ?? 0);
      if (Number.isNaN(pointsAsNumber) || Number.isNaN(rewardBalanceNumber)) {
        setPointsApplied(value);
        return;
      }
      if (pointsAsNumber > rewardBalanceNumber) {
        pointsAsNumber = rewardBalanceNumber;
      }
      if (pointsAsNumber > Cart.subtotal - 1) {
        pointsAsNumber = Cart.subtotal - 1;
      }
      Cart.loyaltyPointsApplied = pointsAsNumber;

      if (pointsAsNumber !== Number(value)) {
        setPointsApplied(pointsAsNumber.toString());
      } else {
        setPointsApplied(value);
      }
    }

    const rewardsBalanceNumber = Number(Cart.rewardsBalance ?? 0);
    let rewardsBalanceDisplay = '';
    if (redemptionType === RedemptionType.cash) {
      rewardsBalanceDisplay = `$${rewardsBalanceNumber.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    } else if (redemptionType === RedemptionType.discount) {
      rewardsBalanceDisplay = `${rewardsBalanceNumber.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })} pts`;
    }

    const ApplyRewardsBlock = (
      <>
        <FormGroup>
          <StyledFormControlLabel
            control={
              <StyledSwitch
                onChange={(event) => {
                  setApplyMyRewards(event.target.checked);
                  if (dposRewardPostAndPreTaxRedemption && !event.target.checked) {
                    setPointsApplied('0');
                    handleCashRedemption(event, true);
                  } else if (event.target.checked) {
                    const maxPoints = Math.min(Cart.subtotal - 1, Number(Cart.rewardsBalance));
                    Cart.loyaltyPointsApplied = maxPoints;
                    setPointsApplied(maxPoints.toString());
                  }
                }}
              />
            }
            label='Apply my rewards'
          />
        </FormGroup>
        {applyMyRewards && redemptionType === RedemptionType.cash && (
          <>
            <PointsTextInput
              onChange={handleCashRedemption}
              value={pointsApplied}
              fullWidth
              type='number'
              inputMode='decimal'
              InputProps={{
                startAdornment: <InputAdornment position='start'>$</InputAdornment>,
              }}
            />
            <NormalText className='fine-print'>Rewards applied cannot be greater than subtotal</NormalText>
          </>
        )}
        {dposRewardRedemption && applyMyRewards && redemptionType === RedemptionType.discount && (
          <>
            <DposApplyRewardsCheckout
              rewards={rewardsArray}
              onApplyReward={handleApplyReward}
              onUndoReward={handleUndoReward}
              rewardsBalanceNumber={rewardsBalanceNumber}
            />
          </>
        )}
      </>
    );

    return (
      <Wrapper>
        <Header>
          <ImageContainer>
            <img src={dispensary.logoImage} alt='' />
          </ImageContainer>
          <Amount>{rewardsBalanceDisplay}</Amount>
          <NormalText>Available</NormalText>
        </Header>
        <Content>
          {dposRewardPostAndPreTaxRedemption && redemptionType === RedemptionType.cash && mode === Mode.cash ? (
            <NormalText className='fine-print'>Redeem loyalty points in-store</NormalText>
          ) : (
            ApplyRewardsBlock
          )}
        </Content>
      </Wrapper>
    );
  }
);

const Wrapper = styled.div`
  border-radius: ${({ theme }) => theme.customized.radius.sm};
  overflow: hidden;
  max-width: 390px;
  display: flex;
  flex-direction: column;
  border: 1px solid #d8dee3;

  .MuiTypography-body1 {
    font-family: ${({ theme }) => theme.customized.fonts.secondary};
  }
`;
const Header = styled.div`
  background-color: ${({ theme }) => theme.customized.colors.buttonsLinks};
  overflow: hidden;
  position: relative;
  width: 100%;
  z-index: 1;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: left;
  padding: 16px 20px;

  &::before {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    content: '';
    height: 316px;
    width: 316px;
    left: -90px;
    pointer-events: none;
    position: absolute;
    z-index: -1;
  }
`;

const Amount = styled.span`
  font-family: ${({ theme }) => theme.customized.fonts.primary};
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0%;
  margin-right: 4px;
`;

const NormalText = styled.span`
  font-weight: 400;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;

  &.fine-print {
    color: ${({ theme }) => theme.colors.grey[45]};
  }
`;

const ImageContainer = styled.div`
  display: block;
  height: 40px;
  width: 40px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 100%;
  }
`;
const Content = styled.div`
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledSwitch = styled(Switch)`
  &.MuiSwitch-root {
    width: 40px;
    height: 24px;
    border-radius: 14px;
    margin-left: 11px;
    margin-right: 12px;
    padding: 0px;

    .MuiSwitch-switchBase.Mui-checked {
      transform: translateX(16px);
    }

    .MuiSwitch-track {
      background-color: ${({ theme }) => theme.colors.grey[75]};
      opacity: 1;
    }
    .Mui-checked + .MuiSwitch-track {
        background-color: ${({ theme }) => theme.customized.colors.buttonsLinks};
        opacity: 1;
      }
    }

    .MuiSwitch-colorSecondary.Mui-checked:hover {
      background: none;
    }
    .MuiButtonBase-root {
      padding: 2px;
    }
    .MuiSwitch-thumb {
      background-color: #fff;
      height: 20px;
      width: 20px;
      box-shadow: none;
    }
    .Mui-checked {
      .MuiSwitch-thumb {
        background-color: #fff;
        background-image: url('/icons/check-small.svg');
        background-size: 9px 7px;
        background-repeat: no-repeat;
        background-position: center;
      }
    }
  }
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  margin-bottom: 12px;
  .MuiTypography-root {
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
    letter-spacing: 0%;
  }
`;

const PointsTextInput = styled(DefaultInput)`
  margin: 0px;
  width: 246px;
  height: 48px;
  padding: 1px;

  .MuiFilledInput-adornedStart {
    padding-left: 22px;
  }

  .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel),
  .MuiInputAdornment-root {
    margin: 0px;

    .MuiTypography-body1 {
      font-family: ${({ theme }) => theme.customized.fonts.primary};
      font-size: 15px;
    }
  }

  font-weight: 400;
  letter-spacing: 0%;

  .MuiInputBase-input {
    padding: 12px;
    padding-left: 0px;
    font-size: 15px;
  }
  .Mui-focused {
    box-shadow: 0px 0px 4px 0px #4c94d780;
  }

  .Mui-focused > input {
    box-shadow: none !important;
    border: none !important;
  }
`;
